import type { MessageDescriptor } from "@lingui/core";

import { i18n } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { makeAutoObservable } from "mobx";

import {
  apiGetClinics,
  apiGetMonitoringSettingsMonitoring,
  apiGetMonitoringSettingsQuestionnaires,
  apiGetPatients,
  apiGetPatientsMinimal,
  apiGetPhysicians,
  apiGetPolicies,
  apiGetStudyText,
  apiPutClinicsUuid,
  apiPutPhysiciansUuid,
  apiPutPoliciesUuid,
  apiPutUsersResetUuid,
  apiPutUsersTemporaryPassUuid,
  apiPutUsersUuid,
  UpdateUserAdminSchema,
  UserPasswordStatus,
} from "../../api";
import { ApiConnector } from "../../api/api-connector";
import { hasAdminAccess } from "../../constants/permissions";
import { APIDataModel } from "../../models/api-data-model";
import { assert } from "../../utils/utils";
import { Patients } from "../patients/patients";
import { FullPatientsItem } from "../patients/patients-tab-model";
import { AccessPolicies } from "./access-policies/access-policies";
import { Clinics } from "./clinics/clinics";
import { PatientStatusIndex } from "./common/patient-status-breakdown";
import {
  loadPhysicians,
  Physicians,
  PhysiciansItem,
  toPhysiciansItem,
} from "./physicians/physicians";
import {
  MonitoringSettings,
  toMonitoringSettingsItem,
} from "./settings/monitoring-settings";
import {
  QuestionnaireSettings,
  toQuestionnaireSettingsItem,
} from "./settings/questionnaire-settings";
import { StudyTexts, toStudyTextsItem } from "./settings/study-texts";
import { Users } from "./users/users";

export class AdminConsole {
  clinics?: Clinics = undefined;
  physicians?: Physicians = undefined;
  users?: Users = undefined;
  accessPolicies?: AccessPolicies = undefined;
  monitoringSettings?: MonitoringSettings = undefined;
  questionnaireSettings?: QuestionnaireSettings = undefined;
  studyTexts?: StudyTexts = undefined;

  tempPasswordPopup = false;
  tempPasswordText = "";

  initialized = false;

  constructor(public api: ApiConnector) {
    makeAutoObservable(this);
  }

  async init() {
    await this.refreshClinics();
    await this.refreshPhysicians();
    await this.refreshUsers();

    // Load access policies and settings (Level 4 only)
    if (hasAdminAccess(this.api.permissions)) {
      await Promise.all([
        this.refreshAccessPolicies(),
        this.refreshMonitoringSettings(),
        this.refreshQuestionnaireSettings(),
        this.refreshStudyTexts(),
      ]);
    }

    this.initialized = true;
  }

  // creating data
  createPatients() {
    const patients = new Patients(
      [],
      this.api.permissions,
      this.api.requireUserUUID(),
    );

    patients.loading = true;
    void apiGetPatientsMinimal()
      .then((res) => {
        patients.data = res.data.patients;
        patients.init();
      })
      .finally(() => {
        patients.loading = false;
      });

    return patients;
  }

  createClinics() {
    const clinics = new Clinics(
      [],
      this.api.permissions,
      this.api.requireUserUUID(),
    );

    clinics.loading = true;
    void apiGetClinics()
      .then((res) => {
        clinics.data = res.data.clinics;
        clinics.init();
      })
      .finally(() => {
        clinics.loading = false;
      });

    return clinics;
  }

  createPhysicians() {
    const physicians = new Physicians(
      [],
      this.api.permissions,
      this.api.requireUserUUID(),
    );

    void loadPhysicians(physicians);

    return physicians;
  }

  createOrphanPhysicians() {
    const m = new APIDataModel<PhysiciansItem>(this.api, async () => {
      const res = await apiGetPhysicians();
      return res.data.physicians
        .map(toPhysiciansItem)
        .filter((p) => !p.assigned_monitoring_users?.length);
    });
    void m.refresh();
    return m;
  }

  /**
   * Patients with no assigned physician. The fetch returns all visible
   * patients (pre-filter), so pass `statusIndex` to also populate a
   * PatientStatusIndex from the same response instead of a second call.
   */
  createOrphanPatients(statusIndex?: PatientStatusIndex) {
    const m = new APIDataModel<FullPatientsItem>(this.api, async () => {
      const res = await apiGetPatients({
        query: { load_type: "basic", paginate: false },
      });
      const patients = res.data.patients as FullPatientsItem[];
      statusIndex?.indexPatients(patients);
      return patients.filter((p) => !p.physicians?.length);
    });
    m.idAttr = "patient_id";
    void m.refresh();
    return m;
  }

  createUsers() {
    const modal = new Users(this.api);

    if (hasAdminAccess(this.api.permissions)) {
      void modal.init();
    } else {
      void modal.initPhysiciansOnly();
    }

    return modal;
  }

  createMonitoringUsers(country?: string, region?: number) {
    const modal = new Users(this.api);
    void modal.initMonitoringUsers(country, region);
    return modal;
  }

  async refreshClinics() {
    const response = await apiGetClinics();
    if (this.clinics) {
      this.clinics.data = response.data.clinics;
    } else {
      this.clinics = new Clinics(
        response.data.clinics,
        this.api.permissions,
        this.api.requireUserUUID(),
      );
    }
    this.clinics.init();
  }

  async refreshPhysicians() {
    this.api.clearResponseCache();
    const response = await apiGetPhysicians();
    const data = response.data.physicians.map(toPhysiciansItem);
    if (this.physicians) {
      this.physicians.data = data;
    } else {
      this.physicians = new Physicians(
        data,
        this.api.permissions,
        this.api.requireUserUUID(),
      );
    }
    this.physicians.init();
  }

  async refreshUsers() {
    if (!this.users) {
      this.users = new Users(this.api);
    }
    await this.users.init();
  }

  async refreshAccessPolicies() {
    const response = await apiGetPolicies();
    if (this.accessPolicies) {
      this.accessPolicies.data = response.data.policies;
    } else {
      this.accessPolicies = new AccessPolicies(
        response.data.policies,
        this.api.permissions,
        this.api.requireUserUUID(),
      );
    }
    this.accessPolicies.init();
  }

  async refreshMonitoringSettings() {
    const response = await apiGetMonitoringSettingsMonitoring();
    const data = response.data.monitoring_settings.map(
      toMonitoringSettingsItem,
    );
    if (this.monitoringSettings) {
      this.monitoringSettings.data = data;
    } else {
      this.monitoringSettings = new MonitoringSettings(
        data,
        this.api.permissions,
        this.api.requireUserUUID(),
      );
    }
    this.monitoringSettings.init();
  }

  async refreshQuestionnaireSettings() {
    const response = await apiGetMonitoringSettingsQuestionnaires();
    const data = response.data.questionnaire_automaton_settings.map(
      toQuestionnaireSettingsItem,
    );
    if (this.questionnaireSettings) {
      this.questionnaireSettings.data = data;
    } else {
      this.questionnaireSettings = new QuestionnaireSettings(
        data,
        this.api.permissions,
        this.api.requireUserUUID(),
      );
    }
    this.questionnaireSettings.init();
  }

  async refreshStudyTexts() {
    const response = await apiGetStudyText();
    const data = response.data.study_texts.map(toStudyTextsItem);
    if (this.studyTexts) {
      this.studyTexts.data = data;
    } else {
      this.studyTexts = new StudyTexts(
        data,
        this.api.permissions,
        this.api.userUUID ?? undefined,
      );
    }
    this.studyTexts.init();
  }

  // updates
  async updateUser(uuid: string, body: UpdateUserAdminSchema) {
    await apiPutUsersUuid({
      path: { uuid: uuid },
      body: body,
    });
    await this.users?.init();
    window.location.reload();
  }

  async resetUser(uuid: string) {
    await apiPutUsersResetUuid({
      path: { uuid: uuid },
      query: { reset_email: true },
    });
    alert(i18n._(msg`Reset email was sent`));
  }

  async temporaryPassword(uuid: string) {
    const response = await apiPutUsersTemporaryPassUuid({
      path: { uuid: uuid },
      query: { token_expiry_timedelta: "P2D" },
    });
    this.tempPasswordText = response.data.temporary_password;
    this.tempPasswordPopup = true;
  }

  async closeTemporaryPasswordPopup() {
    this.tempPasswordPopup = false;
    window.location.reload();
  }

  async updateClinic(uuid: string, body: object) {
    await apiPutClinicsUuid({
      path: { uuid: uuid },
      body: body,
    });
    this.clinics?.init();
    window.location.reload();
  }

  async updatePhysician(uuid: string, body: object) {
    await apiPutPhysiciansUuid({
      path: { uuid: uuid },
      body: body,
    });
    await this.refreshPhysicians();
  }

  async updateAccessPolicy(uuid: string, body: object) {
    await apiPutPoliciesUuid({
      path: { uuid: uuid },
      body: body,
    });
    // Reload access policies after update
    const accessPoliciesResponse = await apiGetPolicies();
    assert(this.accessPolicies, "AccessPolicies model not initialized");
    this.accessPolicies.data = accessPoliciesResponse.data.policies;
    this.accessPolicies.init();
    window.location.reload();
  }
}

export const UserPassStatus: Record<UserPasswordStatus, MessageDescriptor> = {
  [UserPasswordStatus.not_active]: msg`User not active.`,
  [UserPasswordStatus.regular]: msg`User password.`,
  [UserPasswordStatus.reset_by_user]: msg`User requested pass. reset.`,
  [UserPasswordStatus.reset_by_admin]: msg`Admin requested pass. reset.`,
  [UserPasswordStatus.temporary_valid]: msg`Temporary password`,
  [UserPasswordStatus.temporary_expired]: msg`Temporary password - expired.`,
  [UserPasswordStatus.temporary_error]: msg`Temporary password - error - contact admin.`,
};
